import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';

import { CreateBroadcastView, DeleteBroadcastView } from '~src/bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '~src/codegen/Localize';
import { BroadcastErrorCode } from '~src/commonInfra/utils/ErrorCodes';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ILongRunningAction } from '~src/permissions/services/types/ILongRunningAction';
import { IBroadcastableItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';

import { broadcastViewCreated, CreateBroadcastViewAction, DeleteBroadcastViewAction } from '../actions/BroadcastActions';
import { Broadcasts } from '../resources/Broadcasts';

export function* rootBroadcastSaga(): SagaIterator {
    yield takeEvery(CreateBroadcastView, handleCreateBroadcastView);
    yield takeEvery(DeleteBroadcastView, handleDeleteBroadcastView);
}

export function* handleCreateBroadcastView(action: CreateBroadcastViewAction) {
    try {
        yield call(createBroadcast, action.item);
        yield put(broadcastViewCreated(action.item));
    } catch (e) {
        const localizedMessage = errorMessage(e);
        yield put(requestFailed(e, localizedMessage));
    }
}

export function* handleDeleteBroadcastView(action: DeleteBroadcastViewAction) {
    try {
        yield call(deleteBroadcast, action.item);
    } catch (e) {
        yield put(requestFailed(e, Messages.broadcastsError()));
    }
}

export function createBroadcast(item: IBroadcastableItemInfo): Promise<any> {
    return Broadcasts.createBroadcast(item.id, false, false, false)
        .then(onComplete, onFail);
}

export function deleteBroadcast(item: IBroadcastableItemInfo): Promise<any> {
    let result = Broadcasts.deleteBroadcast(item.luid)
        .then((result: IResult) => {
            if (result?.errors?.length) {
                return onFail(result.errors);
            }
            return Promise.resolve(result);
        });
    return result;
}

function onFail(errors: IError[]): Promise<any> {
    return Promise.reject(errors);
}

function onComplete(result: ILongRunningAction): Promise<any> {
    if (result && result.succeeded) {
        return Promise.resolve(result);
    }
    return onFail(result.response && result.response.result.errors);
}

function errorMessage(errors: IError[]): string {
    const code: BroadcastErrorCode = errors[0]?.code;
    const metaData = errors[0]?.errorMetaData?.detailLimit;
    switch (code) {
        case BroadcastErrorCode.BroadcastErrorUnknown:
            return Messages.broadcastsError();
        case BroadcastErrorCode.BroadcastWorkbookDataSourcesAreNotExtracts:
            return Messages.BroadcastWorkbookDataSourcesAreNotExtracts();
        case BroadcastErrorCode.BroadcastWorkbookRowLimitExceeded:
            return Messages.BroadcastWorkbookRowLimitExceeded({ ROW_LIMIT: metaData });
        case BroadcastErrorCode.BroadcastWorkbookSizeLimitExceeded:
            return Messages.BroadcastWorkbookSizeLimitExceeded({ SIZE_LIMIT: metaData });
        case BroadcastErrorCode.BroadcastWorkbookContainsUserFilter:
            return Messages.BroadcastWorkbookContainsUserFilter();
        case BroadcastErrorCode.BroadcastWorkbookContainsCustomSQL:
            return Messages.BroadcastWorkbookContainsCustomSQL();
        case BroadcastErrorCode.BroadcastWorkbookContainsCube:
            return Messages.BroadcastWorkbookContainsCube();
        case BroadcastErrorCode.BroadcastWorkbookContainsScript:
            return Messages.BroadcastWorkbookContainsScript();
        case BroadcastErrorCode.BroadcastWorkbookUnknownValidationError:
            return Messages.BroadcastWorkbookUnknownValidationError();
        case BroadcastErrorCode.CannotOverwriteBroadcast: // User doesn't need set overwrite flag in UI
            return Messages.BroadcastServiceError();
        case BroadcastErrorCode.BroadcastNotFound:
            return Messages.BroadcastNotFound();
        case BroadcastErrorCode.BroadcastServiceError:
            return Messages.BroadcastServiceError();
        default:
            return Messages.broadcastsError();
    }
}